import React, { Component } from "react";
import CRMCampaignService from "../../services/crmcampaignservice";

// CRM campaign status. Reads Admin_CRM_CampaignStatus via the Store WebAPI.
// Styles are scoped under .cs-root so they don't touch the rest of the portal.
//
// Every response number on this page is attributed against THAT CUSTOMER'S OWN
// send time, never a calendar date — a date join credits the campaign with people
// who were already coming back, which is how a flat campaign gets reported as a win.
const DAILY_CAP = 1000;

export default class CRMCampaigns extends Component {
  constructor(props) {
    super(props);
    this.service = new CRMCampaignService();
    this.state = { summary: null, rows: [], conversions: [], loading: true, refreshedAt: null, error: "", tab: "status", convFilter: "all" };
  }

  componentDidMount() {
    this.load();
  }

  load = async () => {
    this.setState({ loading: true, error: "" });
    const s = await this.service.getStatus();
    if (s && s.rows) {
      this.setState({ summary: s.summary || null, rows: s.rows || [], conversions: s.conversions || [], loading: false, refreshedAt: new Date() });
    } else {
      this.setState({ loading: false, error: (s && s.message) || "Could not load campaign status." });
    }
  };

  stamp = (d) =>
    d ? d.toLocaleString("en-GB", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit", hour12: false }) : "";

  inr = (n) => {
    const v = Math.round(Number(n) || 0);
    if (v >= 100000) return "\u20b9" + (v / 100000).toFixed(v % 100000 ? 2 : 0) + "L";
    if (v >= 1000) return "\u20b9" + (v / 1000).toFixed(v % 1000 ? 1 : 0) + "k";
    return "\u20b9" + v;
  };

  state_of = (r) => {
    if (r.sent === 0) return ["wait", "not sent"];
    if (r.pending > 0) return ["live", "sending"];
    return ["done", "sent"];
  };

  pctOf = (a, b) => (b > 0 ? Math.round((a / b) * 100) : 0);

  // Delivery comes from Meta's status callbacks, which we only started recording on
  // 8 Sep. A campaign sent before that has no callbacks at all: show a dash, not 0.
  deliveryCells = (r) => {
    // Campaigns sent before 8 Sep only have a few late read receipts; a percentage
    // off those would say "1% delivered". Below half coverage, show a dash instead.
    if (!r.tracked || r.tracked < r.sent / 2) {
      const t = "Sent before delivery tracking began (8 Sep)" + (r.tracked ? " — only " + r.tracked + " late reports" : "");
      return [<td key="d" className="num dim" title={t}>—</td>, <td key="r" className="num dim" title={t}>—</td>, <td key="u" className="num dim" title={t}>—</td>];
    }
    const part = r.tracked < r.sent ? " of " + r.tracked + " tracked" : "";
    return [
      <td key="d" className="num" title={r.delivered + part + " delivered"}>{r.delivered}<div className="cs-sub">{this.pctOf(r.delivered, r.tracked)}%</div></td>,
      <td key="r" className="num dim">{r.readCount}</td>,
      <td key="u" className={"num" + (r.undelivered > 0 ? " bad" : "")} title="WhatsApp refused to deliver (mostly its limit on marketing messages)">{r.undelivered}<div className="cs-sub">{this.pctOf(r.undelivered, r.tracked)}%</div></td>,
    ];
  };

  renderKpis() {
    const s = this.state.summary || {};
    const capPct = Math.min(100, Math.round(((s.rolling24h || 0) / DAILY_CAP) * 100));
    return (
      <div className="cs-kpis">
        <div className="cs-kpi">
          <div className="cs-n">{s.totalSent || 0}</div>
          <div className="cs-k">messages sent</div>
        </div>
        {s.totalTracked ? (
          <div className="cs-kpi">
            <div className={"cs-n " + (this.pctOf(s.totalDelivered, s.totalTracked) < 70 ? "bad" : "good")}>{this.pctOf(s.totalDelivered, s.totalTracked)}%</div>
            <div className="cs-k">delivered, of {s.totalTracked} tracked · {s.totalUndelivered} blocked by WhatsApp</div>
          </div>
        ) : null}
        <div className="cs-kpi">
          <div className="cs-n">{s.totalPending || 0}</div>
          <div className="cs-k">still queued</div>
        </div>
        <div className="cs-kpi">
          <div className={"cs-n " + (s.totalFailed > 0 ? "bad" : "good")}>{s.totalFailed || 0}</div>
          <div className="cs-k">failures, all time</div>
        </div>
        <div className="cs-kpi">
          <div className="cs-n">{this.inr(s.totalRevenue)}</div>
          <div className="cs-k">revenue after their message</div>
        </div>
        <div className="cs-kpi">
          <div className="cs-n">
            {s.rolling24h || 0}
            <span className="cs-of"> / {DAILY_CAP}</span>
          </div>
          <div className="cs-k">sent in the rolling 24h</div>
          <div className="cs-bar"><i style={{ width: capPct + "%" }} /></div>
        </div>
      </div>
    );
  }

  renderRows() {
    if (this.state.loading) return <tr><td colSpan="14" className="cs-empty">Loading…</td></tr>;
    if (!this.state.rows.length) return <tr><td colSpan="14" className="cs-empty">No campaigns loaded yet.</td></tr>;
    return this.state.rows.map((r) => {
      const [cls, label] = this.state_of(r);
      return (
        <tr key={r.campaign}>
          <td>
            <strong>{r.name}</strong>
            <div className="cs-sub">{r.sentOn ? "sent " + r.sentOn : "scheduled"}</div>
          </td>
          <td><span className={"cs-pill " + cls}>{label}</span></td>
          <td className="num">{r.sent}</td>
          <td className="num dim">{r.pending}</td>
          <td className={"num" + (r.failed > 0 ? " bad" : "")}>{r.failed}</td>
          {this.deliveryCells(r)}
          <td className="num dim">{r.heldBack}</td>
          <td className="num hi">{r.pickups}</td>
          <td className="num hi">{r.ordered}</td>
          <td className="num hi">{this.inr(r.revenue)}</td>
          <td className="num dim">{r.holdoutOrdered}</td>
          <td className="num dim">{this.inr(r.holdoutRevenue)}</td>
        </tr>
      );
    });
  }

  render() {
    const s = this.state.summary || {};
    return (
      <div className="cs-root">
        <style>{CS_CSS}</style>
        <div className="cs-head">
          <div>
            <h1>CRM campaigns · September</h1>
            <p>
              Engagement and reactivation sends, live from production. Every pickup and order below is
              counted only if it happened after that customer's own message.
            </p>
          </div>
          <div className="cs-actions">
            {this.state.tab !== "plan" ? (
              <React.Fragment>
                <button className="cs-refresh" onClick={this.load} disabled={this.state.loading}>
                  {this.state.loading ? "Refreshing…" : "Refresh"}
                </button>
                <div className="cs-stamp">{this.state.refreshedAt ? "as at " + this.stamp(this.state.refreshedAt) : ""}</div>
              </React.Fragment>
            ) : null}
          </div>
        </div>

        <div className="cs-tabs">
          <button className={"cs-tab" + (this.state.tab === "status" ? " on" : "")}
                  onClick={() => this.setState({ tab: "status" })}>Live status</button>
          <button className={"cs-tab" + (this.state.tab === "plan" ? " on" : "")}
                  onClick={() => this.setState({ tab: "plan" })}>The plan</button>
          <button className={"cs-tab" + (this.state.tab === "conv" ? " on" : "")}
                  onClick={() => this.setState({ tab: "conv" })}>Conversions</button>
        </div>

        {this.state.tab === "plan" ? this.renderPlan()
          : this.state.tab === "conv" ? this.renderConversions()
          : this.renderStatus()}
      </div>
    );
  }

  renderPlan() {
    return (
      <div className="cs-planwrap">
        <div className="cs-planbar">
          <span>LaundroKart CRM · plan for review · September 2026</span>
          <a href="/crm/plan.html" target="_blank" rel="noopener noreferrer">Open in a new tab ↗</a>
        </div>
        <iframe className="cs-plan" src="/crm/plan.html" title="LaundroKart CRM plan, September 2026" />
      </div>
    );
  }

  // A pickup with no order raised yet is the "pending" case: the customer booked
  // after their message but the order appears only at collection, so there is no
  // amount yet. One filter feeds the table, the counts AND the CSV - if the export
  // did not match what is on screen, that is the kind of thing nobody notices until
  // it has been acted on.
  visibleConversions = () => {
    const list = this.state.conversions || [];
    const f = this.state.convFilter;
    if (f === "orders") return list.filter((c) => c.kind === "Order");
    if (f === "pending") return list.filter((c) => c.kind === "Pickup");
    if (f === "cancelled") return list.filter((c) => c.kind === "Cancelled" || c.kind === "Re-booked");
    return list;
  };

  downloadCsv = () => {
    const list = this.visibleConversions();
    if (!list.length) return;
    const nameOf = {};
    (this.state.rows || []).forEach((r) => { nameOf[r.campaign] = r.name; });
    const esc = (v) => {
      const s = v === null || v === undefined ? "" : String(v);
      return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    };
    const header = ["Campaign", "Type", "Customer", "Mobile", "Order ID", "Pickup ID", "Amount", "Sent", "Acted", "Pickup date", "Code"];
    const body = list.map((c) => {
      const isOrder = c.kind === "Order";
      return [
        nameOf[c.campaign] || "Campaign " + c.campaign,
        c.kind || "",
        c.customer || "",
        c.mobile || "",
        isOrder ? c.orderID : "",
        isOrder ? "" : c.pickupID,
        isOrder ? Math.round(Number(c.amount) || 0) : "",
        c.sentOn || "",
        c.decidedAt || "",
        c.pickupDate || "",
        c.promocode || "",
      ];
    });
    const csv = [header, ...body].map((r) => r.map(esc).join(",")).join("\r\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "crm-conversions-" + (this.state.convFilter === "all" ? "" : this.state.convFilter + "-") + new Date().toISOString().slice(0, 10) + ".csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  renderConversions() {
    if (this.state.loading) return <div className="cs-conv-empty">Loading…</div>;
    if (this.state.error) return <div className="cs-conv-empty">{this.state.error}</div>;
    const list = this.state.conversions || [];
    const nameOf = {};
    (this.state.rows || []).forEach((r) => { nameOf[r.campaign] = r.name; });
    if (!list.length) {
      return (
        <div className="cs-conv-empty">
          No conversions yet. Customers who place an order after their own message appear here with the real order ID.
        </div>
      );
    }
    const orders = list.filter((c) => c.kind === "Order");
    const pickups = list.filter((c) => c.kind === "Pickup");
    const cancelled = list.filter((c) => c.kind === "Cancelled");
    const rebooked = list.filter((c) => c.kind === "Re-booked");
    const total = orders.reduce((a, c) => a + (Number(c.amount) || 0), 0);
    const shown = this.visibleConversions();
    const f = this.state.convFilter;
    const chip = (key, label, n) => (
      <button key={key} className={"cs-fchip" + (f === key ? " on" : "")}
              onClick={() => this.setState({ convFilter: key })}>
        {label} <span className="cs-fcount">{n}</span>
      </button>
    );
    return (
      <div className="cs-convwrap">
        <div className="cs-convsum">
          <span>
            <b>{orders.length}</b> order{orders.length === 1 ? "" : "s"} · <b>{this.inr(total)}</b> booked
            {pickups.length ? <React.Fragment> · <b>{pickups.length}</b> awaiting collection</React.Fragment> : null}
            {cancelled.length + rebooked.length ? <React.Fragment> · <b>{cancelled.length + rebooked.length}</b> cancelled{rebooked.length ? " (" + rebooked.length + " re-booked)" : ""}</React.Fragment> : null}
            {" "}· counted after each customer's own message
          </span>
          <button className="cs-csv" onClick={this.downloadCsv}>Download CSV</button>
        </div>
        <div className="cs-filters">
          {chip("all", "All", list.length)}
          {chip("orders", "Orders", orders.length)}
          {chip("pending", "Awaiting collection", pickups.length)}
          {chip("cancelled", "Cancelled", cancelled.length + rebooked.length)}
          <span className="cs-fnote">
            {f === "pending"
              ? "Booked and not yet collected — the order is raised at collection, so there is no amount yet."
              : f === "cancelled"
              ? "Cancelled after booking. \u201cRe-booked\u201d means the same customer booked again within a week — that one is counted on its own row."
              : f === "orders" ? "Orders raised after the customer's own message."
              : "Everything attributed after each customer's own message."}
          </span>
        </div>
        <div className="cs-tablewrap">
          <table>
            <thead>
              <tr>
                <th>Campaign</th><th>Type</th><th>Customer</th><th>Mobile</th>
                <th className="r">Reference</th><th className="r">Amount</th>
                <th>Sent</th><th>Acted</th><th>Pickup date</th><th>Code</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((c, i) => {
                const isOrder = c.kind === "Order";
                // The proc reuses the promocode column to carry a note on non-order
                // rows (a cancel reason, or "re-booked as #N"). Render it as a note,
                // never under the Code heading - that column means a promo code.
                const note = isOrder ? null : (c.promocode || null);
                const badge = isOrder ? "cs-b-order"
                  : c.kind === "Cancelled" ? "cs-b-cancel"
                  : c.kind === "Re-booked" ? "cs-b-rebook" : "cs-b-pick";
                return (
                  <tr key={i}>
                    <td className="cs-cn">{nameOf[c.campaign] || "Campaign " + c.campaign}</td>
                    <td><span className={"cs-badge " + badge}>{c.kind || "Pickup"}</span>
                        {note ? <div className="cs-note-sm">{note}</div> : null}</td>
                    <td>{c.customer || "—"}</td>
                    <td className="cs-mono">{c.mobile}</td>
                    <td className="r cs-mono">{isOrder ? "#" + c.orderID : "Pickup #" + c.pickupID}</td>
                    <td className="r cs-amt">{isOrder ? this.inr(c.amount)
                      : c.kind === "Cancelled" ? <span className="cs-canc">cancelled</span>
                      : c.kind === "Re-booked" ? <span className="cs-rebk">re-booked</span>
                      : <span className="cs-pend">awaiting</span>}</td>
                    <td className="cs-dim">{c.sentOn}</td>
                    <td className="cs-dim">{c.decidedAt}</td>
                    <td className="cs-dim">{c.pickupDate || "—"}</td>
                    <td className="cs-mono">{isOrder ? (c.promocode || "\u2014") : "\u2014"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  renderStatus() {
    const s = this.state.summary || {};
    return (
      <div>
        {this.state.error ? <div className="cs-err">{this.state.error}</div> : null}

        {this.renderKpis()}

        <div className="cs-panel">
          <div className="cs-panelh">
            <h2>By campaign</h2>
            <span>Held back = the control group, deliberately not messaged</span>
          </div>
          <div className="cs-tw">
            <table>
              <thead>
                <tr>
                  <th>Campaign</th><th>State</th>
                  <th className="num">Sent</th><th className="num">Queued</th><th className="num">Failed</th>
                  <th className="num">Delivered</th><th className="num">Read</th><th className="num">Not delivered</th>
                  <th className="num">Held back</th><th className="num">Pickups</th>
                  <th className="num">Ordered</th><th className="num">Revenue</th>
                  <th className="num">Holdout ord.</th><th className="num">Holdout rev.</th>
                </tr>
              </thead>
              <tbody>{this.renderRows()}</tbody>
            </table>
          </div>
        </div>

        <div className="cs-note">
          <b>Pickups and orders count only what happened after that customer's own message.</b>{" "}
          {s.excludedBookings || 0} booking(s) since 2 September are excluded because they happened before the
          customer was contacted — those people were already coming back, and counting them would flatter every
          number on this page. Personal codes used so far: <b>{s.codesUsed || 0}</b>.
        </div>
        <div className="cs-note">
          <b>Sent is not delivered.</b> Delivered, Read and Not delivered come from WhatsApp's own delivery
          reports, recorded since 8 September — campaigns sent earlier show a dash. "Not delivered" is almost
          always WhatsApp's limit on marketing messages to people who rarely engage with businesses. Read is a
          floor: people who turn off read receipts never show as read. The held-back comparison still uses
          everyone the message was sent to, which keeps it fair; it just means a campaign that reached only half
          its list will show a diluted effect.
        </div>
        <div className="cs-note">
          <b>This is progress, not the result.</b> The read that matters is lift over the held-back groups once
          each offer window closes — 28 September for campaigns 5 and 6. Comparing a treated column against a
          holdout column this early will mislead you: at these volumes both are expected to sit near zero for a
          fortnight even if the campaign works.
        </div>
        <div className="cs-note">
          <b>Revenue is the order value booked after their message</b> — <code>FinalOrderAmount</code>,
          attributed on the customer's decision time: when they booked the pickup, or when the order was
          raised for a walk-in. It is booked value, not yet collected, and it is not profit — read it next
          to the holdout revenue column, never on its own.
        </div>
      </div>
    );
  }
}

const CS_CSS = `
.cs-root{--cs-card:#fff;--cs-ink:#231a2b;--cs-ink2:#4f4557;--cs-muted:#7a6f83;--cs-faint:#a89fb0;--cs-line:#e8e2ee;--cs-line2:#f0ecf4;--cs-brand:#c71c78;--cs-good:#1f9d5b;--cs-warn:#c07d00;--cs-warnsoft:#fbf0d9;--cs-crit:#c0392b;--cs-critsoft:#fbe7e4;--cs-goodsoft:#e3f5ec;--cs-teal:#0f766e;font-variant-numeric:tabular-nums;color:var(--cs-ink);padding:4px 2px 40px}
.cs-root h1,.cs-root h2{margin:0;font-weight:700}
.cs-head{display:flex;justify-content:space-between;align-items:flex-start;gap:20px;flex-wrap:wrap;margin-bottom:18px}
.cs-head h1{font-size:19px;letter-spacing:-.01em}
.cs-head p{margin:5px 0 0;font-size:12.5px;color:var(--cs-muted);max-width:640px;line-height:1.5}
.cs-actions{text-align:right}
.cs-refresh{border:1px solid var(--cs-line);background:#fff;color:var(--cs-ink);font-size:12px;font-weight:600;padding:7px 16px;border-radius:6px;cursor:pointer}
.cs-refresh:hover:enabled{border-color:var(--cs-brand);color:var(--cs-brand)}
.cs-refresh:disabled{opacity:.55;cursor:default}
.cs-stamp{font-size:10.5px;color:var(--cs-faint);margin-top:6px;letter-spacing:.02em}
.cs-err{background:var(--cs-critsoft);color:var(--cs-crit);border-radius:6px;padding:10px 14px;font-size:12.5px;margin-bottom:16px}
.cs-kpis{display:grid;grid-template-columns:repeat(auto-fit,minmax(170px,1fr));gap:12px;margin-bottom:20px}
.cs-kpi{background:var(--cs-card);border:1px solid var(--cs-line);border-radius:8px;padding:14px 16px}
.cs-n{font-size:26px;font-weight:700;line-height:1.1}
.cs-n.good{color:var(--cs-good)} .cs-n.bad{color:var(--cs-crit)}
.cs-of{font-size:13px;font-weight:600;color:var(--cs-faint)}
.cs-k{font-size:11.5px;color:var(--cs-muted);margin-top:4px}
.cs-bar{height:3px;background:var(--cs-line2);border-radius:2px;margin-top:9px;overflow:hidden}
.cs-bar i{display:block;height:100%;background:var(--cs-brand);border-radius:2px}
.cs-panel{background:var(--cs-card);border:1px solid var(--cs-line);border-radius:8px;overflow:hidden}
.cs-panelh{display:flex;justify-content:space-between;align-items:baseline;gap:14px;padding:14px 16px;border-bottom:1px solid var(--cs-line)}
.cs-panelh h2{font-size:13.5px}
.cs-panelh span{font-size:11px;color:var(--cs-faint)}
.cs-tw{overflow-x:auto}
.cs-root table{width:100%;border-collapse:collapse;min-width:820px}
.cs-root thead th{font-size:10.5px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--cs-muted);text-align:left;padding:12px 14px;border-bottom:1px solid var(--cs-line);background:#faf8fc;white-space:nowrap}
.cs-root tbody td{padding:12px 14px;border-bottom:1px solid var(--cs-line2);vertical-align:middle;font-size:13px}
.cs-root tbody tr:last-child td{border-bottom:none}
.cs-root tbody tr:hover{background:#faf8fc}
.cs-root th.num,.cs-root td.num{text-align:right}
.cs-root td.dim{color:var(--cs-faint)}
.cs-root td.hi{color:var(--cs-teal);font-weight:700}
.cs-root td.bad{color:var(--cs-crit);font-weight:700}
.cs-sub{font-size:11px;color:var(--cs-faint);margin-top:2px}
.cs-pill{display:inline-block;font-size:10px;font-weight:700;letter-spacing:.05em;text-transform:uppercase;padding:3px 8px;border-radius:4px;white-space:nowrap}
.cs-pill.done{background:var(--cs-goodsoft);color:var(--cs-good)}
.cs-pill.live{background:var(--cs-warnsoft);color:var(--cs-warn)}
.cs-pill.wait{background:var(--cs-line2);color:var(--cs-muted)}
.cs-empty{text-align:center;color:var(--cs-faint);padding:26px 0;font-size:12.5px}
.cs-note{background:#faf8fc;border-left:2px solid var(--cs-line);border-radius:6px;padding:12px 15px;font-size:12.5px;color:var(--cs-ink2);line-height:1.55;margin-top:14px}
.cs-note b{color:var(--cs-ink)}
.cs-note code{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:11.5px;background:#efe9f4;padding:1px 5px;border-radius:3px}
.cs-tabs{display:flex;gap:4px;border-bottom:1px solid var(--cs-line);margin-bottom:18px}
.cs-tab{border:0;background:none;font-size:12.5px;font-weight:600;color:var(--cs-muted);padding:9px 14px;cursor:pointer;border-bottom:2px solid transparent;margin-bottom:-1px}
.cs-tab:hover{color:var(--cs-ink)}
.cs-tab.on{color:var(--cs-brand);border-bottom-color:var(--cs-brand)}
.cs-planwrap{background:var(--cs-card);border:1px solid var(--cs-line);border-radius:8px;overflow:hidden}
.cs-planbar{display:flex;justify-content:space-between;align-items:center;gap:14px;padding:11px 16px;border-bottom:1px solid var(--cs-line);font-size:12px;color:var(--cs-muted)}
.cs-planbar a{color:var(--cs-brand);text-decoration:none;font-weight:600}
.cs-planbar a:hover{text-decoration:underline}
.cs-plan{width:100%;height:78vh;min-height:560px;border:0;display:block;background:#fff}
.cs-convwrap{background:var(--cs-card);border:1px solid var(--cs-line);border-radius:8px;overflow:hidden}
.cs-convsum{display:flex;align-items:center;justify-content:space-between;gap:14px;padding:11px 16px;border-bottom:1px solid var(--cs-line);font-size:12.5px;color:var(--cs-ink2)}
.cs-convsum b{color:var(--cs-ink)}
.cs-filters{display:flex;align-items:center;flex-wrap:wrap;gap:8px;padding:10px 16px;border-bottom:1px solid var(--cs-line);background:var(--cs-card)}
.cs-fchip{border:1px solid var(--cs-line);background:var(--cs-card);color:var(--cs-ink2);font-size:12px;font-weight:600;padding:5px 12px;border-radius:20px;cursor:pointer;display:inline-flex;align-items:center;gap:7px}
.cs-fchip:hover{border-color:var(--cs-brand);color:var(--cs-brand)}
.cs-fchip.on{background:var(--cs-brand);border-color:var(--cs-brand);color:#fff}
.cs-fcount{font-variant-numeric:tabular-nums;font-size:11px;opacity:.75}
.cs-fnote{font-size:11.5px;color:var(--cs-faint);margin-left:4px}
.cs-b-cancel{background:var(--cs-critsoft);color:var(--cs-crit)}
.cs-b-rebook{background:var(--cs-warnsoft);color:var(--cs-warn)}
.cs-note-sm{font-size:10.5px;color:var(--cs-faint);margin-top:3px;max-width:190px;line-height:1.35}
.cs-canc{color:var(--cs-crit);font-weight:600}
.cs-rebk{color:var(--cs-warn);font-weight:600}
.cs-csv{flex:0 0 auto;border:1px solid var(--cs-brand);background:#fff;color:var(--cs-brand);font-size:12px;font-weight:600;padding:7px 14px;border-radius:6px;cursor:pointer;white-space:nowrap}
.cs-csv:hover{background:var(--cs-brand);color:#fff}
.cs-tablewrap{overflow-x:auto}
.cs-conv-empty{background:var(--cs-card);border:1px solid var(--cs-line);border-radius:8px;text-align:center;color:var(--cs-muted);padding:34px 16px;font-size:13px}
.cs-root .cs-convwrap td.r,.cs-root .cs-convwrap th.r{text-align:right}
.cs-mono{font-variant-numeric:tabular-nums;white-space:nowrap}
.cs-amt{font-weight:700;color:var(--cs-good)}
.cs-dim{color:var(--cs-muted);white-space:nowrap}
.cs-cn{font-weight:600;color:var(--cs-brand);white-space:nowrap}
.cs-badge{display:inline-block;font-size:10.5px;font-weight:700;letter-spacing:.02em;padding:2px 8px;border-radius:10px;white-space:nowrap}
.cs-b-order{background:var(--cs-goodsoft);color:var(--cs-good)}
.cs-b-pick{background:var(--cs-warnsoft);color:var(--cs-warn)}
.cs-pend{color:var(--cs-warn);font-style:italic}
`;
